import { MuiToggleButton, MuiToggleButtonGroup } from '@fox/atoms/src/mui-toggle-button/index';
import { FlexRow } from '@fox/layout/src/flex';
import { darken, getLuminance, lighten } from 'polished';
import { getAttributeValues, transformLegacyProps } from '../../../../utils';
import { IQuestionAnswer } from '../../../form-root/contracts';
import { FormRenderMode } from '../../../form-runtime-context/contracts';
import { useFormRuntimeContext } from '../../../form-runtime-context/form-runtime-context';
import { getHoveredOrSelectedColour } from '../../transaction-block/colour-helpers';
import { QuestionBlockProps, QuestionChoice } from '../contracts';

interface IToggleOptionProps {
	options: QuestionChoice[];
	orientation?: 'vertical' | 'horizontal';
	currentValue: string | undefined;
	id: string;
	fieldProps: QuestionBlockProps;
}

const alignDictionary = {
	left: 'start',
	center: 'center',
	right: 'end'
};

export const ToggleOption = (props: IToggleOptionProps) => {
	const { fieldProps, options, currentValue, orientation } = props;
	const questionName = fieldProps.name;
	const { formRenderMode, answerQuestion } = useFormRuntimeContext();

	const style = fieldProps.style ?? {};

	const {
		attributes: { padding, margin, horizontalAlign = 'center' }
	} = transformLegacyProps(fieldProps.type, fieldProps); //type doesnt even matter here

	const { top: paddingTop, bottom: paddingBottom, right: paddingRight, left: paddingLeft } = getAttributeValues(padding);
	const { top: marginTop, bottom: marginBottom, right: marginRight, left: marginLeft } = getAttributeValues(margin);

	let { color, backgroundColor, hoverBackgroundColour, selectedColor: selectedBackgroundColor } = style;

	let hoverSelectedBackgroundColor = hoverBackgroundColour;
	let hoverSelectedColor;
	let selectedColor;
	let hoverColor;

	if (backgroundColor && color) {
		const colorLum = getLuminance(color);
		const backLum = getLuminance(backgroundColor);
		const luminanceDiff = Math.abs(backLum - colorLum);
		if (luminanceDiff < 0.4) {
			// console.log('Luminance difference is too small, adjusting color for better contrast', color, backgroundColor, colorLum);
			color = colorLum ? darken(0.8, color) : lighten(0.8, color);
		}

		if (!selectedBackgroundColor) {
			selectedBackgroundColor = getHoveredOrSelectedColour(backgroundColor, 0.2);
		}

		selectedColor = color;
		const selectedLuminanceDiff = Math.abs(getLuminance(selectedBackgroundColor) - getLuminance(color));
		if (selectedLuminanceDiff < 0.4) {
			selectedColor = getHoveredOrSelectedColour(color, 0.8);
		}

		hoverColor = color;

		if (!hoverBackgroundColour) {
			hoverBackgroundColour = getHoveredOrSelectedColour(backgroundColor, 0.3);
		}
		const hoverLuminanceDiff = Math.abs(getLuminance(hoverBackgroundColour) - getLuminance(hoverColor));
		if (hoverLuminanceDiff < 0.4) {
			if (hoverBackgroundColour === color) {
				hoverColor = backgroundColor;
			} else {
				hoverColor = getHoveredOrSelectedColour(color, 0.8);
			}
		}

		if (!hoverSelectedBackgroundColor) {
			hoverSelectedBackgroundColor = getHoveredOrSelectedColour(backgroundColor, 0.5);
		}

		if (!hoverSelectedColor) {
			hoverSelectedColor = getHoveredOrSelectedColour(hoverColor, 0.2);
		}
		if (Math.abs(getLuminance(hoverSelectedColor) - getLuminance(hoverSelectedBackgroundColor)) < 0.4) {
			hoverSelectedColor = getHoveredOrSelectedColour(hoverColor, 0.2);
		}
	}

	const paddingObj = {
		paddingTop,
		paddingLeft,
		paddingBottom,
		paddingRight
	};
	const marginObj = {
		marginLeft,
		marginTop,
		marginRight,
		marginBottom
	};

	return (
		<FlexRow style={{ width: '100%', justifyContent: horizontalAlign ? alignDictionary[horizontalAlign] : 'start' }}>
			<MuiToggleButtonGroup
				exclusive
				orientation={orientation === 'horizontal' ? 'horizontal' : 'vertical'}
				style={{ width: 'fit-content', ...marginObj }}
			>
				{options.map((o, i) => {
					return (
						<MuiToggleButton
							key={o.id}
							value={o}
							style={{
								fontFamily: style?.fontFamily ?? undefined,
								fontSize: style?.fontSize ?? undefined,
								fontWeight: style?.fontWeight ?? undefined,
								fontStyle: style?.fontStyle ?? undefined,
								textDecoration: style?.textDecoration ?? undefined,
								borderColor: style?.borderColor ?? undefined,
								borderStyle: style?.borderSize ? 'solid' : undefined,
								borderWidth: style?.borderSize ?? undefined,
								...paddingObj
							}}
							sx={{
								color,
								backgroundColor,
								textTransform: 'none',
								'&:hover': {
									color: hoverColor,
									backgroundColor: hoverBackgroundColour
								},
								'&:hover.Mui-selected': {
									color: hoverSelectedColor,
									backgroundColor: hoverSelectedBackgroundColor
								},
								'&.Mui-selected': {
									color: selectedColor,
									backgroundColor: selectedBackgroundColor
								}
							}}
							selected={o.value === currentValue}
							onClick={(e, v: QuestionChoice) => {
								if (formRenderMode === FormRenderMode.design) {
									return;
								}
								const newAnswer: IQuestionAnswer = {
									questionName: questionName,
									answer: { answerString: v.value }
								};
								answerQuestion(newAnswer);
							}}
						>
							{o.displayName}
						</MuiToggleButton>
					);
				})}
			</MuiToggleButtonGroup>
		</FlexRow>
	);
};
